import {
  VaultListItemButton,
  ErrorListItem,
  ListContentIsland,
  ListItem,
  SkeletonListItem,
  TextField,
  Collapse,
  NoVaultsListItem,
} from '@razz-team/ui-web';
import { ReactNode, useDeferredValue, useMemo, useState } from 'react';
import { ApiVault } from '../../../openapi';
import { useTranslation } from 'react-i18next';
import { TransitionGroup } from 'react-transition-group';

export function VaultList({
  title,
  actions,
  vaults,
  error,
  isLoading,
  isSearchEnabled = false,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  vaults: Array<ApiVault> | undefined;
  error: Error | null | undefined;
  isLoading: boolean;
  isSearchEnabled?: boolean;
}) {
  const { t } = useTranslation();

  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);

  const filteredVaults = useMemo(() => {
    const q = deferredQuery.trim().toLocaleLowerCase();
    if (q === '') return vaults;
    return vaults?.filter((vault) => {
      if (vault.id === q) return true;
      if (vault.balance.currency.toLocaleLowerCase().startsWith(q)) return true;
      return false;
    });
  }, [vaults, deferredQuery]);

  const hasResults = (filteredVaults?.length ?? 0) > 0;

  return (
    <ListContentIsland title={title} actions={actions}>
      {isSearchEnabled && (
        <ListItem>
          <TextField
            type="search"
            label={t('Search')}
            size="small"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            fullWidth
          />
        </ListItem>
      )}
      {isLoading ? (
        <>
          <SkeletonListItem variant="withButton" />
          <SkeletonListItem variant="withButton" />
          <SkeletonListItem variant="withButton" />
        </>
      ) : error ? (
        <ErrorListItem error={error} />
      ) : hasResults ? (
        <TransitionGroup>
          {filteredVaults?.map((vault) => (
            <Collapse key={vault.id} in timeout={100}>
              <ListItem disablePadding>
                <VaultListItemButton<'a'> vault={vault} href={`/account/vaults/${vault.id}`} />
              </ListItem>
            </Collapse>
          ))}
        </TransitionGroup>
      ) : (
        <NoVaultsListItem />
      )}
    </ListContentIsland>
  );
}
